import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@mui/styles';
import {
  Box,
  colors,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Typography
} from '@mui/material';
import Modal from '@mui/material/Modal';
import CancelIcon from '@mui/icons-material/Cancel';
import { ReactSortable } from 'react-sortablejs';
import CardComponent from '../../components/CardComponent';
import ButtonPanel from './components/ButtonPanel';
import { charts, CUBE_FILTERS } from '../../utils/constants';
import {
  applyFilters,
  gePortfoliosWithManagers,
  getFilters,
  getIndicators
} from '../../actions/filters';
import { getFunderRatingCategories } from '../../actions';
import 'antd/dist/antd.css';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <Typography
      component="div"
      role="tabpanel"
      hidden={value !== index}
      id={`scrollable-auto-tabpanel-${index}`}
      style={{
        background: '#fafafa',
        boxShadow:
          '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)'
      }}
      aria-labelledby={`scrollable-auto-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={3}>{children}</Box>}
    </Typography>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired
};

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1
  },
  tab: {
    borderRadius: '10px 10px 0px 0px',
    marginRight: '2px',
    textTransform: 'none',
    fontSize: 16,
    fontWeight: 100,
    lineHeight: '1.2em'
  },
  paper: {
    padding: theme.spacing(2),
    textAlign: 'center',
    color: theme.palette.text.secondary
  },
  body: {
    width: '75%',
    maxWidth: 900,
    backgroundColor: 'white',
    left: '50%',
    position: 'absolute',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    borderRadius: 10,
    padding: 20
  },
  close: {
    cursor: 'pointer',
    position: 'absolute',
    right: 20,
    top: 20
  },
  cardWrapper: {
    width: '32.5%',
    float: 'left',
    marginRight: '0.8%'
  },
  cardWrapperHalf: {
    width: '49%',
    float: 'left',
    marginRight: '0.8%'
  },
  cardWrapperLast: {
    width: '100%',
    float: 'left'
  },
  title: {
    textAlign: 'center',
    fontFamily: 'Roboto',
    fontSize: '2.50em',
    fontWeight: 100,
    lineHeight: '1.2em',
    marginBottom: theme.spacing(3)
  },
  betaTitle: {
    position: 'absolute',
    top: 30,
    left: 300,
    fontWeight: 400,
    lineHeight: '1.2em',
    color: '#f8984e',
    fontSize: '1.8em'
  },
  formControl: {
    width: 600,
    backgroundColor: '#fafafa',
    marginLeft: 20
  },
  '@global': {
    '.MuiTabs-root': {
      background: '#fafafa'
    },
    '.MuiTab-textColorPrimary': {
      background: colors.orange[100],
      color: '#f8984e',
      borderLeft: 'solid 1px',
      borderRight: 'solid 1px',
      borderTop: 'solid 1px',
      borderColor: colors.orange[200],
      borderRightColor: colors.orange[200],
      minWidth: 'unset',
      borderRadius: '5px 5px 0px 0px',
      height: 30
    },
    '.MuiTab-textColorPrimary.Mui-selected': {
      background: '#f8984e',
      color: '#fafafa'
    },
    '.MuiTabs-flexContainer': {
      borderBottom: '1px solid #e0e0e0'
    },
    '.MuiTypography-root': {
      fontSize: '1rem',
      color: 'black'
    }
  }
}));

// MuiTypography-root MuiTypography-body1
export default function DashboardLegacy() {
  const dispatch = useDispatch();
  const classes = useStyles();

  const [value, setValue] = React.useState(0);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [modalData, setModalData] = React.useState(null);
  const [selectedTemplate, setSelectedTemplate] = React.useState(null);

  React.useEffect(() => {
    dispatch(gePortfoliosWithManagers());
    dispatch(getIndicators());
    dispatch(getFilters());
    dispatch(getFunderRatingCategories());
  }, [dispatch]);

  const filters = useSelector(state => state.filters);
  const ratingCategories = useSelector(state => state.funder.ratingCategories);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const toggleModal = () => {
    setModalOpen(!modalOpen);
  };

  const onPressModalOpen = props => {
    setModalData(props);
    toggleModal();
  };

  const [listSummary, setListSummary] = React.useState([
    { ...charts.total_inv_amt, onPressModalOpen },
    { ...charts.total_reached, onPressModalOpen },
    { ...charts.total_si, onPressModalOpen },
    { ...charts.si_by_indicator, onPressModalOpen }
  ]);

  const [listKPI, setListKPI] = React.useState([
    { ...charts.sroi, onPressModalOpen },
    { ...charts.leverage, onPressModalOpen },
    { ...charts.data_quality, onPressModalOpen }
  ]);

  const [listReport, setListReport] = React.useState([
    { ...charts.compliance, onPressModalOpen },
    { ...charts.feedback, onPressModalOpen },
    { ...charts.ratings, onPressModalOpen }
  ]);

  const getCardClass = (index, lastIndex, style = classes.cardWrapper) => {
    if (index === lastIndex) {
      return classes.cardWrapperLast;
    }
    return style;
  };

  const getQuery = oldQuery => {
    const query = { ...oldQuery };
    query.filters = [...query.filters, ...filters.appliedFilters];
    return query;
  };

  const getCubeFilter = (dimension, values) => {
    return {
      dimension,
      operator: 'equals',
      values
    };
  };

  const handleApply = template => {
    const cubeFilters = [];
    const {
      portfolioIds,
      portfolioYears,
      relationshipMgrs,
      siIndicators
    } = template;
    if (portfolioIds.length > 0) {
      cubeFilters.push(getCubeFilter(CUBE_FILTERS.PORTFOLIOS_ID, portfolioIds));
    }
    if (portfolioYears.length > 0) {
      cubeFilters.push(
        getCubeFilter(CUBE_FILTERS.PORTFOLIOS_YEAR, portfolioYears)
      );
    }
    if (relationshipMgrs.length > 0) {
      cubeFilters.push(getCubeFilter(CUBE_FILTERS.USERS_ID, relationshipMgrs));
    }
    if (siIndicators.length > 0) {
      cubeFilters.push(
        getCubeFilter(CUBE_FILTERS.INDICATORS_NAME, siIndicators)
      );
    }
    dispatch(applyFilters(cubeFilters));
  };

  const handleChangeSelectFilter = event => {
    setSelectedTemplate(event.target.value);
    const template = event.target.value
      ? event.target.value.template
      : {
          portfolioIds: 0,
          portfolioYears: 0,
          relationshipMgrs: 0,
          siIndicators: 0
        };
    handleApply(template);
  };

  return (
    <Paper className={classes.root} elevation={0}>
      <Tabs
        value={value}
        onChange={handleChange}
        indicatorColor="primary"
        textColor="primary"
      >
        <Tab label="Investment Summary" className={classes.tab} />
        <Tab label="Investment KPIs" className={classes.tab} />
        <Tab label="Report Management" className={classes.tab} />
        <Tab label="All boards" className={classes.tab} />
        <FormControl className={classes.formControl}>
          <InputLabel>Select Saved Filter Set</InputLabel>
          <Select value={selectedTemplate} onChange={handleChangeSelectFilter}>
            <MenuItem value="">All</MenuItem>
            {filters.databaseFilters.map(filter => (
              <MenuItem key={filter.id} value={filter}>
                {filter.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <ButtonPanel filters={filters} />
      </Tabs>
      <TabPanel value={value} index={0}>
        <div className={classes.root}>
          <ReactSortable
            tag="table"
            list={listSummary}
            setList={setListSummary}
          >
            {listSummary.map((cardData, index) => (
              <div key={cardData.resourceId} className={getCardClass(index, 3)}>
                <CardComponent {...cardData} query={getQuery(cardData.query)} />
              </div>
            ))}
          </ReactSortable>
        </div>
      </TabPanel>
      <TabPanel value={value} index={1}>
        <div className={classes.root}>
          <ReactSortable tag="table" list={listKPI} setList={setListKPI}>
            {listKPI.map(cardData => (
              <div key={cardData.resourceId} className={classes.cardWrapper}>
                <CardComponent {...cardData} query={getQuery(cardData.query)} />
              </div>
            ))}
          </ReactSortable>
        </div>
      </TabPanel>
      <TabPanel value={value} index={2}>
        <div className={classes.root}>
          <ReactSortable tag="table" list={listReport} setList={setListReport}>
            {listReport.map((cardData, index) => (
              <div
                key={cardData.resourceId}
                className={getCardClass(index, 2, classes.cardWrapperHalf)}
              >
                <CardComponent
                  {...cardData}
                  query={getQuery(cardData.query)}
                  ratingCategories={ratingCategories}
                />
              </div>
            ))}
          </ReactSortable>
        </div>
      </TabPanel>
      <TabPanel value={value} index={3}>
        <div className={classes.root}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            style={{ textAlign: 'center', marginTop: 15 }}
          >
            - Investment Summary -
          </Typography>
          <Paper className={classes.paper}>
            <ReactSortable
              tag="table"
              list={listSummary}
              setList={setListSummary}
            >
              {listSummary.map((cardData, index) => (
                <div key={cardData.resourceId} className={getCardClass(index)}>
                  <CardComponent
                    {...cardData}
                    query={getQuery(cardData.query)}
                  />
                </div>
              ))}
            </ReactSortable>
          </Paper>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            style={{ textAlign: 'center', marginTop: 15 }}
          >
            - Investment KPIs -
          </Typography>
          <Paper className={classes.paper}>
            <ReactSortable tag="table" list={listKPI} setList={setListKPI}>
              {listKPI.map(cardData => (
                <div key={cardData.resourceId} className={classes.cardWrapper}>
                  <CardComponent
                    {...cardData}
                    query={getQuery(cardData.query)}
                  />
                </div>
              ))}
            </ReactSortable>
          </Paper>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            style={{ textAlign: 'center', marginTop: 15 }}
          >
            - Report Management -
          </Typography>
          <Paper className={classes.paper}>
            <ReactSortable
              tag="table"
              list={listReport}
              setList={setListReport}
            >
              {listReport.map(cardData => (
                <div key={cardData.resourceId} className={classes.cardWrapper}>
                  <CardComponent
                    {...cardData}
                    query={getQuery(cardData.query)}
                  />
                </div>
              ))}
            </ReactSortable>
          </Paper>
        </div>
      </TabPanel>
      <Modal
        open={modalOpen}
        onClose={() => console.log('close')}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
      >
        <div className={classes.body}>
          {modalData && <CardComponent {...modalData} />}
          <CancelIcon onClick={toggleModal} className={classes.close} />
        </div>
      </Modal>
    </Paper>
  );
}
